import { useSetAtom } from 'jotai'
import DashboardWrapper from './DashboardWrapper'
import { userTeamAtom } from '../../store/user.store'
import { updateTeam } from '../../firestore'
import { useOutletContext, useParams } from 'react-router-dom'
import { Input } from '../input'
import { useState, lazy } from 'react'
import { Button } from '../button'
import { faBolt, faPlus } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useInviteTeamMember, useTeamMembersMetadata } from '@/entities/team'
import type { Team } from '@rundown-studio/types'
import { UserRole } from '@rundown-studio/types'
import {
  useDashboardPermission,
  DashboardPermissionGuard,
  DashboardPermissions,
} from '@/entities/user'
import { cn } from '@/utils'
import type { User } from 'firebase/auth'
import type { AddTeamMemberPayload } from '@/features/add-team-member-modal'
import { TeamTable } from '@/features/team-table/team-table'
import { useModal } from '@/features/modal-manager'

const UpgradeModal = lazy(() => import('../modal/UpgradeModal'))
const AddTeamMemberModal = lazy(
  () => import('@/features/add-team-member-modal/ui/add-team-member-modal'),
)

export default function TeamComponent() {
  const { team: activeTeam, plan } = useOutletContext<{
    team: Team
    user: User
    // TODO: Fix type for plan
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    plan: any
  }>()
  const { teamId } = useParams()
  const setActiveTeam = useSetAtom(userTeamAtom)
  const [newTeamName, setNewTeamName] = useState<string>(activeTeam.name)
  const [loadingTeamName, setLoadingTeamName] = useState<boolean>(false)
  const [showAddTeamMemberModal, setShowAddTeamMemberModal] = useState(false)

  const [upgradeModal, renderUpgradeModal] = useModal(UpgradeModal)

  const { isLoading: isLoadingMembersMetadata } = useTeamMembersMetadata({
    params: {
      teamId: teamId as string,
    },
  })
  const { mutate: inviteTeamMember, isPending: isInvitingTeamMember } =
    useInviteTeamMember()

  const loadingTeamWithMeta = isLoadingMembersMetadata || isInvitingTeamMember

  const isTeamAdmin = useDashboardPermission({
    permissions: DashboardPermissions.TEAM_ADMIN,
  })

  const teamMemberRunningTotal =
    Object.entries(activeTeam?.members || {}).length +
    Object.entries(activeTeam?.pending || {}).length
  const teamMemberLimit = plan?.limits?.members || 1
  const exceededTeamMemberLimit =
    teamMemberLimit === -1 ? false : teamMemberRunningTotal >= teamMemberLimit

  const handleAddTeamMember = async (newTeamMember: AddTeamMemberPayload) => {
    inviteTeamMember(
      {
        teamId: teamId as string,
        payload: newTeamMember,
      },
      {
        onSuccess() {
          setShowAddTeamMemberModal(false)
        },
      },
    )
  }

  const handleUpdateTeamName = async () => {
    setLoadingTeamName(true)
    await updateTeam(teamId as string, { name: newTeamName })
    setActiveTeam({ ...activeTeam, name: newTeamName })
    setLoadingTeamName(false)
  }

  return (
    <DashboardWrapper title="Team">
      <div className="grid grid-cols-2">
        <Input
          label="Team name"
          value={newTeamName}
          onChange={setNewTeamName}
          placeholder="My team name"
          loading={loadingTeamName}
          disabled={!isTeamAdmin}
          onBlur={(e) => {
            if (e?.target?.value === activeTeam.name) return
            void handleUpdateTeamName()
          }}
        />
      </div>

      <div className="mt-10">
        <h2 className="text-3xl">Team members</h2>
        <div
          className={cn(
            'my-6 px-4 py-3 flex flex-col gap-6 bg-gray-900 rounded',
            loadingTeamWithMeta && 'bg-animated from-gray-900 to-gray-800',
          )}
        >
          <TeamTable teamId={teamId as string} />
        </div>

        <DashboardPermissionGuard role={UserRole.TEAM_ADMIN}>
          <div className="flex items-center gap-6">
            <Button
              text="Add new member"
              icon={faPlus}
              onClick={() => {
                if (exceededTeamMemberLimit) return upgradeModal.show()
                setShowAddTeamMemberModal(true)
              }}
              toolTip={
                exceededTeamMemberLimit ? (
                  <>
                    Limit reached
                    <FontAwesomeIcon
                      icon={faBolt}
                      className="text-yellow-500"
                    />{' '}
                    Upgrade for more
                  </>
                ) : (
                  ''
                )
              }
            />
            {teamMemberLimit === -1 ? null : (
              // Skip show message if user has unlimited team members
              <p className="text-sm text-gray-400">
                You&apos;ve added {teamMemberRunningTotal} of {teamMemberLimit}{' '}
                members
              </p>
            )}
          </div>
        </DashboardPermissionGuard>

        {renderUpgradeModal({
          message: "You've reached your limit for team members.",
        })}

        <AddTeamMemberModal
          open={showAddTeamMemberModal}
          setOpen={setShowAddTeamMemberModal}
          onAddTeamMember={handleAddTeamMember}
          loading={isInvitingTeamMember}
        />
      </div>
    </DashboardWrapper>
  )
}
